import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { LoadCaseService } from '../../services/load-case.service';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Subscription, lastValueFrom } from 'rxjs';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';

@Component({
    selector: 'app-wellhead-slump',
    templateUrl: './wellhead-slump.component.html',
    standalone: false
})
export class WellheadSlumpComponent implements OnInit, OnDestroy {
  public wellheadSlumpForm: UntypedFormGroup;
  public formationDisplacements: { measuredDepth: number, formationDisplacement: number }[] = [];
  public units: { long: string, short: string };
  public columnDefinitions: { header: string, field: string }[];
  public isLoading = signal<boolean>(false);
  public tableHeight: string;
  public slumpValidation: { min: number, max: number };
  public tooltip = 'Upward Movement (+)/Downward Movement (-)';
  private _subscriptions: Subscription = new Subscription();

  public newDataRow(): FormGroup {
    return new FormGroup({
      measuredDepth: new FormControl(null, [Validators.required]),
      formationDisplacement: new FormControl(null, [Validators.required])
    });
  }

  public inputFields = [{
    name: 'measuredDepth',
    minFractions: 2,
    maxFractions: 6,
    formatDecimals: 2
  },
  {
    name: 'formationDisplacement',
    minFractions: 2,
    maxFractions: 6,
    formatDecimals: 2
  }];

  @Input()
  private componentId: string;

  public get wellheadSlumpControl(): UntypedFormControl {
    return this.wellheadSlumpForm.get('wellheadSlump') as UntypedFormControl;
  }

  constructor(
    private _loadCaseService: LoadCaseService,
    private _formBuilder: UntypedFormBuilder,
    private _storeService: StoreService,
    private _messenger: MediatorService
  ) { }

  async ngOnInit(): Promise<void> {
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.units = {
      long: uu.longLengths,
      short: uu.shortLengths
    };

    if (this.units.short == 'in') {
      this.slumpValidation = { min: -100, max: 100 };
    } else if (this.units.short == 'cm') {
      this.slumpValidation = { min: -254, max: 254 };
    } else {
      this.slumpValidation = { min: -2540, max: 2540 };
    }

    this.columnDefinitions = [
      { header: `Measured Depth (${this.units.long})`, field: 'measuredDepth' },
      { header: `Displacement (${uu.shortLengths})`, field: 'formationDisplacement' },
    ];
    this.isLoading.set(true);
    this.createReactiveForm();

    const wellheadSlump = await lastValueFrom(this._loadCaseService.getWellheadSlump());
    this.wellheadSlumpForm.patchValue(wellheadSlump, { emitEvent: false });

    this.wellheadSlumpForm.controls.applyDisplacementToPostInstallLoads.disable({ emitEvent: false });
    this.wellheadSlumpForm.controls.applyDisplacementToPostInstallLoads.setValue(true, { emitEvent: false });

    this.formationDisplacements = [
      { measuredDepth: 10000, formationDisplacement: 0 }
    ];
    this.isLoading.set(false);
  }

  private createReactiveForm(): void {
    this.wellheadSlumpForm = this._formBuilder.group({
      id: new UntypedFormControl(''),
      applyDisplacementToPostInstallLoads: new UntypedFormControl(true),
      wellheadSlump: new UntypedFormControl({ value: 0 }, { validators: [Validators.min(this.slumpValidation.min), Validators.max(this.slumpValidation.max)], updateOn: 'blur' }),
      // formationDisplacements: new FormControl(this.formationDisplacements, [Validators.required, Validators.minLength(1)]),
    });

    this._subscriptions.add(this.wellheadSlumpForm.valueChanges.subscribe(async () => {
      if (this.wellheadSlumpForm.valid) {
        await lastValueFrom(this._loadCaseService.updateWellheadSlump(this.wellheadSlumpForm.getRawValue()));
        this._messenger.publish(new DataSavedMessage('Wellbore Displacement'));
      }
    }));
  }

  public formationChange(event: any) {
    this.formationDisplacements = event.dataRows;
    this.wellheadSlumpForm.controls.formationDisplacements.patchValue(this.formationDisplacements);
  }

  ngOnDestroy(): void {
    this._subscriptions?.unsubscribe();
  }
}
